/* eslint-disable react/jsx-no-bind */
import React, { Component } from 'react';
import {
  cloneDeep,
  difference,
  find,
  findIndex,
  first,
  get,
  isEmpty,
  isEqual,
  pull,
  remove,
  set,
  some,
  uniq,
  includes,
  unionBy,
  sortBy,
  identity,
  map,
} from 'lodash';
import { Button, Form, Icon, Table } from 'semantic-grab-ui/src';
import { Row, Col, Checkbox, Alert } from 'grab-ui';
import FormInputWithError from 'components/form/FormInputWithError';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import {
  WARNING_LIMIT,
  MESSAGE,
  VARIABLE_NAME,
  REQUIRE_VARIABLE,
} from './constants/campaign';

interface Props {
  model: TridentCampaignDetails;
  className: string;
  currentNode: string;
  limits: Record<string, any>;
  node: Record<string, any>;
  pageName: string;
  listRuleTypes: Record<string, any>;
  handleLimitChange: (event: any, data: any) => void;
  eventTypes: string[];
  onModelChange: (_?: Record<string, any>) => void;
}

interface State {
  columnNames: string[] | null;
  rowNames: string[] | null;
  rows: Record<string, any> | null;
  visibleRows: string[] | null;
  limitVarOptions: Array<{ text: string; value: string }>;
}

export default class LimitsTable extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      columnNames: null,
      rowNames: null,
      rows: null,
      visibleRows: null,
      limitVarOptions: [],
    };
  }

  componentDidMount() {
    this.fetchLimitValues();
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(prevProps.limits, this.props.limits) || !isEqual(prevProps.eventTypes, this.props.eventTypes)) {
      this.fetchLimitValues();
    }
  }

  getUniqRowNames = (nodeLimits: any[] = []) => {
    return uniq(nodeLimits.map(x => x.rowName))
      .sort()
      .reverse();
  }

  fetchLimitValues = () => {
    const { currentNode, limits, model, listRuleTypes, eventTypes } = this.props;
    const node = model.nodes[currentNode];
    const eventObjects = get(listRuleTypes, model.targetUserType, []).filter(
      x => includes(eventTypes, x.eventType),
    );

    let allLimitVars: any[] = [];
    map(eventObjects, e => {
      allLimitVars = unionBy(allLimitVars, e.limitVars, l => l.variableName);
    });

    if (allLimitVars) {
      this.setState({
        limitVarOptions: (allLimitVars).map(x => {
          return {
            text: x.noun,
            value: x[VARIABLE_NAME],
          };
        }),
      });
    }

    const nodeLimits =
      (node.type === 'scenarios' ? limits.scenario : limits.action) || [];
    const columnNames = sortBy(
      uniq(nodeLimits.map(field => field.colName)),
      identity
    ); // sort the column names so that "per campaign" always appears before "per user"
    const rows = this.getRows(nodeLimits, !isEmpty(allLimitVars));
    const rowNames = Object.keys(rows);

    const limitValues = get(node, 'limits', []);

    // Check if monthly limits feature flag is enabled
    const isMonthlyLimitsEnabled = get(window, 'Grab.config.ENABLE_MONTHLY_LIMITS', false);

    // Identify monthly limit rows by checking if the key contains Monthly or matches monthly limit types
    const monthlyLimitKeys = ['totalMonthly', 'userMonthly'];
    const monthlyLimitRows = nodeLimits.filter(x =>
      monthlyLimitKeys.includes(x.key) ||
      (x.rowName && x.rowName.toLowerCase().includes('monthly'))
    );

    // mandatory fields should stand before others
    let visibleRows = uniq([
      ...this.getUniqRowNames(nodeLimits.filter(x => x.mandatory === true)),
      ...this.getUniqRowNames(
        nodeLimits.filter(x => find(limitValues, { per: x.key })),
      ),
    ]);

    // Add monthly limit rows if feature flag is enabled
    if (isMonthlyLimitsEnabled && monthlyLimitRows.length > 0) {
      const monthlyRowNames = this.getUniqRowNames(monthlyLimitRows);
      visibleRows = uniq([...visibleRows, ...monthlyRowNames]);
    }

    this.setState({
      columnNames,
      rowNames,
      rows,
      visibleRows,
    });
  };

  getRows = (nodeLimits: any[], hasVarOptions: boolean) => {
    const rows: Record<string, any> = {};

    nodeLimits.forEach(field => {
      if (field[REQUIRE_VARIABLE] && !hasVarOptions) {
        return;
      }
      const data = {
        [field.colName]: {
          key: field.key,
          mandatory: field.mandatory,
          [REQUIRE_VARIABLE]: field[REQUIRE_VARIABLE],
        },
      };
      rows[field.rowName] = { ...rows[field.rowName], ...data };
    });
    return rows;
  };

  updateLimits = (
    updatedModel: TridentCampaignDetails,
    node: Record<string, any>,
    limits: any[],
  ) => {
    if (!isEmpty(limits)) {
      set(node, 'limits', limits);
    } else {
      delete node.limits;
    }
    this.props.onModelChange(updatedModel);
  };

  removeLimitValues = (rowName: string) => {
    const updatedModel = cloneDeep(this.props.model);
    const node = updatedModel.nodes[this.props.currentNode];
    const limits = get(node, 'limits', []);
    const rows = this.state.rows;
    if (rows) {
      Object.values(rows[rowName]).forEach((row: any) => {
        remove(limits, { per: row.key });
      });
    }

    this.updateLimits(updatedModel, node, limits);
  };

  handleCapLimitVariable = (event: any, data: any) => {
    const updatedModel = cloneDeep(this.props.model);
    const node = updatedModel.nodes[this.props.currentNode];
    const per = data.name;
    const value = data.value;

    const limits = get(node, 'limits', []);
    const index = findIndex(limits, { per });

    if (index > -1) {
      limits[index] = { ...limits[index], [VARIABLE_NAME]: value };
    } else {
      limits.push({
        per,
        [VARIABLE_NAME]: value,
      });
    }

    this.updateLimits(updatedModel, node, limits);
  };

  handleLimitChange = (event: any, data: any) => {
    const updatedModel = cloneDeep(this.props.model);
    const node = updatedModel.nodes[this.props.currentNode];
    const per = data.name;
    const limit = data.value;
    const newChild = {
      per,
      limit,
    };
    const limits = get(node, 'limits', []);
    const index = findIndex(limits, { per });
    const isLimitEmpty = limit === null || limit === undefined || limit === '';

    if (index > -1 && isLimitEmpty) {
      limits.splice(index, 1);
    } else if (index === -1 && !isLimitEmpty) {
      limits.push(newChild);
    } else if (index !== -1 && !isLimitEmpty) {
      limits[index] = { ...limits[index], per, limit };
    }

    this.updateLimits(updatedModel, node, limits);
  };

  handleLimitSelect = (event: any, data: any) => {
    const visibleRows = this.state.visibleRows;
    if (!visibleRows) return;
    const newAddedRows = visibleRows.map(rowName => {
      if (rowName === data.name) return data.value;
      return rowName;
    });
    this.setState({ visibleRows: newAddedRows });
    this.removeLimitValues(data.name);
  };

  addLimitRow = () => {
    const visibleRows = this.state.visibleRows;
    const rowNames = this.state.rowNames;
    if (!visibleRows || !rowNames) return;
    const newAddedRows = [...visibleRows];
    const availableRows = difference(rowNames, visibleRows);
    if (isEmpty(availableRows)) return;

    const firstRow = first(availableRows);
    if (firstRow) {
      newAddedRows.push(firstRow);
      this.setState({ visibleRows: newAddedRows });
    }
  };

  removeLimitRow = (rowName: string) => {
    const visibleRows = this.state.visibleRows;
    if (!visibleRows) return;
    const newAddedRows = [...visibleRows];
    pull(newAddedRows, rowName);
    this.setState({ visibleRows: newAddedRows });
    this.removeLimitValues(rowName);
  };

  /**
   * handle unlimited checkbox
   * @param {string} inputKey input's key of checkbox
   */
  handleUnlimitedCheckbox = (e: React.ChangeEvent<HTMLInputElement>, inputKey: string) => {
    const checked = e.target.checked;
    const simulation = {
      name: inputKey,
      value: checked ? 0 : undefined,
    };
    this.handleLimitChange({}, simulation);
  };

  renderRow = (rowName: string) => {
    const rowNames = this.state.rowNames;
    const visibleRows = this.state.visibleRows;
    const rows = this.state.rows;
    const columnNames = this.state.columnNames;

    if (!rowNames || !visibleRows || !rows || !columnNames) return null;

    const availableRows = [
      rowName,
      ...difference(rowNames, visibleRows),
    ];
    const availableOptions = availableRows.map(option => ({
      value: option,
      text: option,
    }));
    const rowData = rows[rowName];
    const isMonthlyLimitsEnabled = get(window, 'Grab.config.ENABLE_MONTHLY_LIMITS', false);
    const isMonthlyLimitRow = rowName && rowName.toLowerCase().includes('monthly');
    const mandatoryRow = some(Object.values(rowData), { mandatory: true }) ||
                         (isMonthlyLimitsEnabled && isMonthlyLimitRow);

    return (
      <Table.Row key={rowName}>
        <Table.Cell>
          {mandatoryRow ? (
            rowName
          ) : (
            <FormInputWithError name={rowName} {...this.props}>
              <Form.Select
                options={availableOptions}
                value={rowName}
                onChange={this.handleLimitSelect}
              />
            </FormInputWithError>
          )}
        </Table.Cell>
        {columnNames.map(
          columnName => columnName && this.renderValueCell(rowData, columnName),
        )}
        <Table.Cell>
          {!mandatoryRow && (
            <Button
              basic
              color="red"
              icon={<Icon name="minus" color="red" />}
              onClick={() => this.removeLimitRow(rowName)}
            />
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  renderValueCell = (rowData: any, columnName: string) => {
    // without rendering if cell data is empty
    if (!Object.keys(rowData).includes(columnName)) {
      return <Table.Cell />;
    }

    const { limitVarOptions } = this.state;
    const node = this.props.model.nodes[this.props.currentNode];
    const limitValues = get(node, 'limits', []);
    const key = get(rowData, `${columnName}.key`, null);
    const value = get(find(limitValues, { per: key }), 'limit');
    const variableNameValue = get(
      find(limitValues, { per: key }),
      VARIABLE_NAME,
    );
    const isUnlimited = +value === 0;
    const isWarning = value >= WARNING_LIMIT;
    const isRequireVariableSelection =
      limitVarOptions.length > 0 &&
      get(rowData, `["${columnName}"].${REQUIRE_VARIABLE}`, false);

    return (
      <Table.Cell key={key}>
        <Row>
          <Col span={9}>
            <FormInputWithError name={key} {...this.props}>
              <Form.Input
                style={{ width: '90%' }}
                required
                type="number"
                min={0}
                value={value}
                onChange={this.handleLimitChange}
                disabled={isUnlimited}
              />
            </FormInputWithError>
          </Col>
          <Col span={6} style={{ marginTop: 10 }}>
            <Checkbox
              checked={isUnlimited}
              onChange={e => this.handleUnlimitedCheckbox(e, key)}
            >
              Unlimited
            </Checkbox>
          </Col>
          {isRequireVariableSelection && (
            <Col span={4}>
              <FormInputWithError name={key} {...this.props}>
                <Form.Select
                  options={limitVarOptions}
                  value={variableNameValue}
                  onChange={this.handleCapLimitVariable}
                />
              </FormInputWithError>{' '}
            </Col>
          )}
        </Row>
        {isWarning && (
          <Row style={{ marginTop: 5 }}>
            <Col span={12}>
              <Alert message={MESSAGE.WARNING_LIMIT} type="warning" />
            </Col>
          </Row>
        )}
      </Table.Cell>
    );
  };

  render() {
    const { columnNames, rowNames, visibleRows } = this.state;

    if (!columnNames || !rowNames || !visibleRows) return null;

    return (
      <Table className={this.props.className}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            {columnNames.map(column => (
              <Table.HeaderCell key={column}>{column}</Table.HeaderCell>
            ))}
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visibleRows.map(row => this.renderRow(row))}
          {!isEmpty(difference(rowNames, visibleRows)) && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Button
                  basic
                  color="green"
                  icon={<Icon name="plus" color="green" />}
                  onClick={this.addLimitRow}
                />
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    );
  }
}
